import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { object, string, array } from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import QUERIES from 'modules/common/queries';
import { IAITextQuestionnaire, IInterviewQuestion, TasksApi } from 'api';
import { useLocalization } from 'localization';

import notifyHandler from 'utils/notifyHandler';
import { FormInterviewState, InterviewState } from 'constants/enums/status';

export const useInterview = <T extends IInterviewQuestion[] | IAITextQuestionnaire>({
  isQuestionnaire = false,
  onError,
  taskId: customTaskId,
  setIsSubmitting,
}: {
  isQuestionnaire?: boolean;
  onError?: () => void;
  taskId?: string;
  setIsSubmitting?: (isSubmitting: boolean) => void;
}) => {
  const { t } = useLocalization();
  const [searchParams] = useSearchParams();

  const isQuestionnaireWithinModal = isQuestionnaire && !!customTaskId;

  const id = customTaskId || searchParams.get('id');
  const [formState, setFormState] = useState<InterviewState>(id ? FormInterviewState.EMPTY : FormInterviewState.ERROR);

  const formSchema = object().shape({
    answers: array().of(
      object({
        question: isQuestionnaireWithinModal
          ? string().trim()
          : string().trim().required(t('validation.mixed.required')),
        answer: isQuestionnaireWithinModal ? string().trim() : string().trim().required(t('validation.mixed.required')),
      })
    ),
  });

  const form = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      answers: [],
    },
    mode: 'onChange',
  });

  const { handleSubmit, getValues, setValue, control } = form;

  const { data: questionData, isLoading: isQuestionLoading } = useQuery({
    queryKey: [isQuestionnaire ? QUERIES.questionnaire : QUERIES.interview, id],
    queryFn: async () => {
      const response = isQuestionnaire
        ? await TasksApi.getAITextQuestionnaire(id as string)
        : await TasksApi.getInterviewQuestions(id as string);

      if (!response) {
        setFormState(FormInterviewState.ERROR);
        return;
      }

      const questions = 'questions' in response ? response.questions : response;

      if (!questions.length) {
        setFormState(FormInterviewState.ALREADY_SUBMITTED);
        return;
      }

      const formattedQuestions = questions.map(item => ({
        question: item.question ?? '',
        answer: item.yesOrNo ? t('common.terms.yes') : '',
      }));
      setValue('answers', formattedQuestions);

      setFormState(FormInterviewState.PENDING);

      return response as T;
    },
    enabled: !!id,
  });

  const { mutateAsync, isPending: isSubmitLoading } = useMutation({
    mutationFn: TasksApi.saveInterviewQuestions,
    onSuccess: result => {
      if (isQuestionnaire) return;

      if (result.success) {
        notifyHandler({ type: 'success', content: t('users.pages.interview.successTitle') });
        setFormState(FormInterviewState.ALREADY_SUBMITTED);
        return;
      }
      notifyHandler({ type: 'error', content: t('users.pages.interview.error') });
    },
  });

  useEffect(() => {
    setIsSubmitting?.(isSubmitLoading);
  }, [isSubmitLoading]);

  const onSubmit = async () => {
    // to be able to control execution flow when provided
    // as reference
    let validationPassed = false;
    await handleSubmit(
      async () => {
        validationPassed = true;
        const values = getValues();
        await mutateAsync({
          answers: (values.answers ?? []).map(answer => ({
            question: answer.question ?? '',
            answer: answer.answer ?? '',
          })),
          taskId: id ?? '',
          isQuestionnaire,
        });
      },
      () => {
        validationPassed = false;
      }
    )();
    return validationPassed;
  };

  return {
    form,
    control,
    options: [t('common.terms.yes'), t('common.terms.no')],
    action: {
      onSubmit,
    },
    setValue,
    apiState: {
      isSubmitLoading,
      isQuestionLoading,
      questionData,
    },
    formState,
  };
};
